import { ReactNode } from 'react'

import { Box, SxProps, Typography, useMediaQuery, useTheme } from '@mui/material'
import { AutomationTagIds } from 'assets/test/test-automation-tag-id'
import { Loading } from 'components/ui'
import { Paper } from 'oneui'

import {
  sectionActionsDesktopSx,
  sectionActionsMobileSx,
  sectionArticleSxLarge,
  sectionArticleSxSmall,
  sectionContentSxLarge,
  sectionContentSxSmall,
  sectionDescriptionSxLarge,
  sectionDescriptionSxSmall,
  sectionHeadlineSx,
  sectionLoadingSx,
  sectionPaperSx,
  sectionTitleContainerSxLarge,
  sectionTitleContainerSxSmall
} from './styled'

type Props = {
  headline?: string
  title?: ReactNode | string
  description?: ReactNode | string
  actions?: ReactNode
  isLoading?: boolean
  size?: 'small' | 'large'
  sx?: SxProps
  paperSx?: SxProps
  articleSx?: SxProps
  titleSx?: SxProps
  sectionTitleSx?: SxProps
  descriptionSx?: SxProps
  contentSx?: SxProps
  actionsSx?: SxProps
  headlineSx?: SxProps
  headlineFontSx?: SxProps
  minHeight?: object | string
  children?: ReactNode
}

// TODO: Modify the title and description to appear when loading
function Section({
  headline,
  title,
  description,
  actions,
  isLoading,
  size = 'large',
  sx,
  paperSx,
  articleSx,
  sectionTitleSx,
  titleSx,
  descriptionSx,
  contentSx,
  actionsSx,
  headlineSx,
  headlineFontSx,
  minHeight = '200px',
  children
}: Props) {
  const theme = useTheme()
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <Box id='SectionContainer' sx={sx}>
      {headline && (
        <Box id='SectionHeadline' sx={{ ...sectionHeadlineSx, ...headlineSx } as SxProps}>
          <Typography
            variant='sectionHeadline'
            sx={{ ...headlineFontSx }}
            data-testid={AutomationTagIds['section.headline'] + '-' + headline}
          >
            {headline}
          </Typography>
        </Box>
      )}
      <Paper id='SectionPaper' sx={{ ...sectionPaperSx, ...paperSx }}>
        {isLoading ? (
          <Box id='SectionLoading' sx={{ ...sectionLoadingSx, minHeight: minHeight }}>
            <Loading />
          </Box>
        ) : (
          <Box
            id='SectionArticle'
            sx={
              {
                ...(size === 'small' ? sectionArticleSxSmall : sectionArticleSxLarge),
                minHeight: minHeight,
                ...articleSx
              } as SxProps
            }
          >
            {title && (
              <Box
                id='SectionTitle'
                sx={
                  {
                    ...(size === 'small' ? sectionTitleContainerSxSmall : sectionTitleContainerSxLarge),
                    ...sectionTitleSx
                  } as SxProps
                }
              >
                <Typography
                  variant={size === 'small' ? 'sectionTitleSmall' : 'sectionTitle2'}
                  sx={{ ...titleSx }}
                  data-testid={AutomationTagIds['section.heading'] + '-' + title}
                  aria-level={1}
                  role='heading'
                >
                  {title}
                </Typography>
              </Box>
            )}
            {description && (
              <Box id='SectionDescription' sx={{ ...(size === 'small' ? sectionDescriptionSxSmall : sectionDescriptionSxLarge) }}>
                {typeof description === 'string' ? (
                  <Typography
                    textAlign={size === 'small' ? 'center' : 'inherit'}
                    sx={{ ...descriptionSx }}
                    data-testid={AutomationTagIds['section.description'] + '-' + description}
                  >
                    {description}
                  </Typography>
                ) : (
                  description
                )}
              </Box>
            )}
            {children && (
              <Box
                data-testid={AutomationTagIds['section.content']}
                id='SectionContent'
                sx={{ ...(size === 'small' ? sectionContentSxSmall : sectionContentSxLarge), ...contentSx } as Props}
              >
                {children}
              </Box>
            )}
            {actions && !isMobileView && (
              <Box id='SectionActions' sx={{ ...sectionActionsDesktopSx, ...actionsSx } as SxProps}>
                {actions}
              </Box>
            )}
          </Box>
        )}
      </Paper>
      {actions && isMobileView && !isLoading && (
        <Box id='SectionActions' sx={{ ...sectionActionsMobileSx, ...actionsSx } as SxProps}>
          {actions}
        </Box>
      )}
    </Box>
  )
}

export default Section
